/**
 * Shared byte-budgeted cache for the mosaic pipeline's two expensive,
 * re-derivable-but-not-cheap artifacts per (ts, LOD): the DECODED raw grid
 * (fusionDecoder.ts's StationDecodeResult — a fetch + TIFF decode) and,
 * additionally keyed by (despeckle, sigma), the field-SMOOTHED grid
 * (mosaicGlFieldSmoothing.ts's fieldSmooth() output — measured at
 * 15-300ms+ even with the fast box-blur approximation, mosaicGlFieldSmoothing.
 * ts's own PERFORMANCE doc comment). One shared ByteBudgetedLruCache
 * instance (frameCache.ts) holds BOTH kinds of entry under ONE ~96MB total
 * budget (not 96MB each) — "scrubbing back over a visited frame must cost
 * zero fetch+decode" and "toggling sigma must not re-blur a visited frame"
 * are the same underlying requirement (never recompute what's still in the
 * budget), so one shared pool naturally balances between them by actual
 * recency of use rather than two independently-sized pools that could
 * starve each other by a fixed split.
 *
 * Both MosaicController (CPU path) and MosaicGlController (GL path) use
 * `mosaicFrameCache` for decode results — sharing one instance means
 * switching the lab panel's render mode between "Mosaic" and "Mosaic GL"
 * never re-fetches/re-decodes a frame the OTHER engine already has warm.
 * MosaicController never touches the smoothed-grid half at all (it has no
 * smoothing concept); MosaicGlController is the only writer of "smoothed"
 * entries.
 */
import type { OverviewLevel } from "./fusionGeo";
import type { StationDecodeResult } from "./fusionDecoder";
import { ByteBudgetedLruCache } from "./frameCache";

/** ~96MB — big enough to hold several full IFD0 (5888x3328, ~19.6MB raw)
 * frames at once (the CONUS mosaic's largest LOD), small enough to stay a
 * clearly bounded, deliberate memory budget rather than "however much the
 * browser tolerates." */
export const MOSAIC_CACHE_BUDGET_BYTES = 96 * 1024 * 1024;

export type MosaicCacheEntry =
  | { kind: "decode"; result: StationDecodeResult }
  | { kind: "smoothed"; raw: Uint8Array; width: number; height: number };

export function decodeCacheKey(ts: string, level: OverviewLevel): string {
  return `RAW:MOSAIC:${ts}:${level}`;
}

/** `despeckle`/`sigma` are part of the key because they change the OUTPUT
 * bytes, not just a display parameter — a smoothed grid for one toggle
 * combination is a different artifact from another's, not a repaint of the
 * same one (contrast floorOrder/palette/sampling, which are pure GPU-side
 * render parameters that never touch this cache at all). */
export function smoothCacheKey(ts: string, level: OverviewLevel, despeckle: boolean, sigma: number): string {
  return `SMOOTH:MOSAIC:${ts}:${level}:${despeckle}:${sigma}`;
}

export function getDecodeResult(cache: ByteBudgetedLruCache<MosaicCacheEntry>, ts: string, level: OverviewLevel): StationDecodeResult | undefined {
  const entry = cache.get(decodeCacheKey(ts, level));
  return entry?.kind === "decode" ? entry.result : undefined;
}

export function setDecodeResult(cache: ByteBudgetedLruCache<MosaicCacheEntry>, ts: string, level: OverviewLevel, result: StationDecodeResult): void {
  cache.set(decodeCacheKey(ts, level), { kind: "decode", result }, result.raw.byteLength);
}

export function getSmoothedGrid(
  cache: ByteBudgetedLruCache<MosaicCacheEntry>,
  ts: string,
  level: OverviewLevel,
  despeckle: boolean,
  sigma: number,
): { raw: Uint8Array; width: number; height: number } | undefined {
  const entry = cache.get(smoothCacheKey(ts, level, despeckle, sigma));
  return entry?.kind === "smoothed" ? entry : undefined;
}

export function setSmoothedGrid(
  cache: ByteBudgetedLruCache<MosaicCacheEntry>,
  ts: string,
  level: OverviewLevel,
  despeckle: boolean,
  sigma: number,
  raw: Uint8Array,
  width: number,
  height: number,
): void {
  cache.set(smoothCacheKey(ts, level, despeckle, sigma), { kind: "smoothed", raw, width, height }, raw.byteLength);
}

/** The one shared cache instance — both controllers import and use this
 * directly (not their own private instance) so decode results are actually
 * shared between them, per this file's own top doc comment. */
export const mosaicFrameCache = new ByteBudgetedLruCache<MosaicCacheEntry>(MOSAIC_CACHE_BUDGET_BYTES);
